import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  navyLight: '#0b2981',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const PROXY = API_BASE;

function getImageUrl(type, partNo, colorId) {
  if (type === 'MINIFIG' || type === 'M') {
    return `https://img.bricklink.com/ItemImage/MN/0/${partNo}.png`;
  }
  return `https://img.bricklink.com/ItemImage/PN/${colorId}/${partNo}.png`;
}

function PartOut() {
  const navigate = useNavigate();

  // ─── Set lookup state ───
  const [setNumber, setSetNumber] = useState('');
  const [setData, setSetData] = useState(null);
  const [parts, setParts] = useState([]);
  const [minifigs, setMinifigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copies, setCopies] = useState(1);

  // Editable quantities: { index: qty }
  const [partQtys, setPartQtys] = useState({});
  const [figQtys, setFigQtys] = useState({});

  // ─── Queue state ───
  const [queue, setQueue] = useState([]); // [{ partNo, name, type, colorId, color, qty, price, condition, fromSet }]
  const [queuedSets, setQueuedSets] = useState([]); // ["10297-1 Boutique Hotel", ...]

  // ─── Inventory ───
  const [inventory, setInventory] = useState([]);
  const [invLoaded, setInvLoaded] = useState(false);

  // ─── Push state ───
  const [pushing, setPushing] = useState(false);
  const [pushProgress, setPushProgress] = useState(0);
  const [pushStatus, setPushStatus] = useState(null);
  const [pushResults, setPushResults] = useState(null);

  // ─── View mode ───
  const [view, setView] = useState('lookup'); // 'lookup' | 'queue' | 'results'

  useEffect(() => {
    fetch(`${PROXY}/api/inventories`)
      .then(res => res.json())
      .then(data => { setInventory(data.data || []); setInvLoaded(true); })
      .catch(err => console.error('Inventory fetch error:', err));
  }, []);

  // ─── Set Lookup ───
  async function lookupSet() {
    if (!setNumber.trim()) return;
    setLoading(true);
    setError(null);
    setSetData(null);
    setParts([]);
    setMinifigs([]);
    setPartQtys({});
    setFigQtys({});
    setCopies(1);
    try {
      const setNo = setNumber.trim().includes('-') ? setNumber.trim() : `${setNumber.trim()}-1`;
      const [setRes, subsetRes] = await Promise.all([
        fetch(`${PROXY}/api/sets/${setNo}`),
        fetch(`${PROXY}/api/sets/${setNo}/subsets`),
      ]);
      const setJson = await setRes.json();
      const subsetJson = await subsetRes.json();
      if (!setJson.data) throw new Error('Set not found');
      setSetData(setJson.data);
      const subsets = subsetJson.data || [];
      const partList = [];
      const figList = [];
      subsets.forEach(matchGroup => {
        const entries = matchGroup.entries || [];
        entries.forEach(entry => {
          const item = entry.item || {};
          const type = item.type || 'PART';
          const obj = {
            partNo: item.no || '',
            name: item.name || 'Unknown',
            type,
            colorId: entry.color_id || 0,
            color: entry.color_name || '',
            qty: entry.quantity || 1,
            extraQty: entry.extra_quantity || 0,
            price: 0.05,
          };
          if (type === 'MINIFIG' || type === 'M') {
            figList.push(obj);
          } else if (type === 'PART' || type === 'P') {
            partList.push(obj);
          }
        });
      });
      setParts(partList);
      setMinifigs(figList);
      // Set default quantities
      const pq = {};
      partList.forEach((_, i) => { pq[i] = partList[i].qty; });
      setPartQtys(pq);
      const fq = {};
      figList.forEach((_, i) => { fq[i] = figList[i].qty; });
      setFigQtys(fq);
    } catch (err) {
      setError('Could not find that set. Check the number and try again.');
    }
    setLoading(false);
  }

  // ─── Update quantities when copies change ───
  function handleCopiesChange(val) {
    const newCopies = Math.max(1, val);
    const oldCopies = copies;
    setCopies(newCopies);
    // Scale quantities proportionally
    const pq = {};
    parts.forEach((p, i) => {
      pq[i] = p.qty * newCopies;
    });
    setPartQtys(pq);
    const fq = {};
    minifigs.forEach((f, i) => {
      fq[i] = f.qty * newCopies;
    });
    setFigQtys(fq);
  }

  // ─── Add to Queue ───
  function addToQueue() {
    if (!setData) return;
    const newItems = [];

    parts.forEach((part, i) => {
      const qty = partQtys[i] ?? part.qty * copies;
      if (qty <= 0) return;
      newItems.push({
        partNo: part.partNo,
        name: part.name,
        type: part.type,
        colorId: part.colorId,
        color: part.color,
        qty,
        price: part.price,
        condition: 'N',
      });
    });

    minifigs.forEach((fig, i) => {
      const qty = figQtys[i] ?? fig.qty * copies;
      if (qty <= 0) return;
      newItems.push({
        partNo: fig.partNo,
        name: fig.name,
        type: fig.type,
        colorId: fig.colorId,
        color: fig.color,
        qty,
        price: fig.price,
        condition: 'N',
      });
    });

    // Merge into existing queue
    const merged = [...queue];
    newItems.forEach(item => {
      const key = `${item.partNo}|${item.colorId}`;
      const existing = merged.find(q => `${q.partNo}|${q.colorId}` === key);
      if (existing) {
        existing.qty += item.qty;
      } else {
        merged.push({ ...item });
      }
    });

    setQueue(merged);
    setQueuedSets(prev => [...prev, `${setData.no} ${setData.name}`]);

    // Reset lookup for next set
    setSetNumber('');
    setSetData(null);
    setParts([]);
    setMinifigs([]);
    setPartQtys({});
    setFigQtys({});
    setCopies(1);
  }

  function removeFromQueue(idx) {
    setQueue(prev => prev.filter((_, i) => i !== idx));
  }

  function updateQueueQty(idx, val) {
    setQueue(prev => prev.map((item, i) => i === idx ? { ...item, qty: Math.max(0, val) } : item));
  }

  function clearQueue() {
    setQueue([]);
    setQueuedSets([]);
  }

  // ─── Inventory helpers ───
  function findExistingLot(partNo, colorId) {
    return inventory.find(lot =>
      (lot.item?.no || '').toUpperCase() === partNo.toUpperCase() &&
      (lot.color_id || 0) === colorId
    );
  }

  function findExistingBins(partNo, colorName) {
    const seen = new Set();
    return inventory.filter(lot =>
      (lot.item?.no || '') === partNo &&
      (lot.color_name || '').toLowerCase() === (colorName || '').toLowerCase() &&
      (lot.remarks || '') !== ''
    ).reduce((acc, lot) => {
      if (!seen.has(lot.remarks)) {
        seen.add(lot.remarks);
        acc.push({ bin: lot.remarks, qty: lot.quantity || 0 });
      }
      return acc;
    }, []);
  }

  function renderBinBadge(partNo, colorName) {
    const bins = findExistingBins(partNo, colorName);
    if (bins.length > 0) {
      return (
        <span style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '4px',
          padding: '2px 7px',
          fontSize: '0.58rem',
          fontWeight: 700,
          color: '#16a34a',
        }}>→ {bins[0].bin}</span>
      );
    }
    return (
      <span style={{
        background: '#faf5ff',
        border: '1px solid #d8b4fe',
        borderRadius: '4px',
        padding: '2px 7px',
        fontSize: '0.58rem',
        fontWeight: 700,
        color: '#7c3aed',
      }}>New</span>
    );
  }

  // ─── Push to BrickLink ───
  async function pushToBrickLink() {
    const validQueue = queue.filter(item => item.qty > 0);
    if (validQueue.length === 0) return;

    setPushing(true);
    setPushProgress(0);
    setPushStatus('Preparing...');
    setView('results');

    // Refresh inventory for accurate merge detection
    try {
      const invRes = await fetch(`${PROXY}/api/inventories`);
      const invJson = await invRes.json();
      setInventory(invJson.data || []);
    } catch (e) {}

    const results = { mergeOk: 0, newOk: 0, errors: [] };
    let done = 0;

    for (const item of validQueue) {
      const existing = findExistingLot(item.partNo, item.colorId);
      const blType = item.type === 'MINIFIG' || item.type === 'M' ? 'MINIFIG' : 'PART';

      if (existing) {
        // Merge — send only the delta (qty to add)
        setPushStatus(`Merging ${item.partNo} (${item.color})...`);
        try {
          const res = await fetch(`${PROXY}/api/inventories/${existing.inventory_id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ quantity: item.qty }),
          });
          if (!res.ok) throw new Error(await res.text());
          results.mergeOk++;
        } catch (err) {
          results.errors.push(`Merge ${item.partNo}: ${err.message}`);
        }
      } else {
        // Create new lot → -INCOMING
        setPushStatus(`Creating ${item.partNo} (${item.color})...`);
        try {
          const payload = {
            item: { no: item.partNo, type: blType },
            color_id: item.colorId,
            quantity: item.qty,
            unit_price: item.price,
            new_or_used: item.condition || 'N',
            remarks: '-INCOMING',
            is_retain: true,
            is_stock_room: false,
          };
          const res = await fetch(`${PROXY}/api/inventories`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });
          if (!res.ok) throw new Error(await res.text());
          results.newOk++;
        } catch (err) {
          results.errors.push(`New ${item.partNo}: ${err.message}`);
        }
      }
      done++;
      setPushProgress(Math.round((done / validQueue.length) * 100));
    }

    setPushResults(results);
    setPushing(false);
    setPushStatus(null);
  }

  // ─── Launch Stocking Mode ───
  function launchStocking() {
    const stockingParts = queue.filter(item => item.qty > 0).map(item => {
      const existing = findExistingLot(item.partNo, item.colorId);
      return {
        partNo: item.partNo,
        name: item.color + ' ' + item.partNo,
        type: item.type === 'MINIFIG' || item.type === 'M' ? 'M' : 'P',
        color: item.color,
        colorId: item.colorId,
        qty: item.qty,
        bin: existing?.remarks || '-INCOMING',
      };
    });
    if (stockingParts.length > 0) {
      localStorage.setItem('stockingParts', JSON.stringify(stockingParts));
      localStorage.setItem('stockingSource', 'Part Out');
      navigate('/stocking');
    }
  }

  // ─── Render: Push Results ───
  if (view === 'results' && (pushResults || pushing)) {
    if (pushing) {
      return (
        <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
          <PageHeader title="Part Out" icon="⊟" />
          <div style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '40px 24px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.8rem', marginBottom: '12px', color: colors.navy }}>⟳</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: colors.textPrimary, marginBottom: '16px' }}>
              {pushStatus || 'Pushing to BrickLink...'}
            </div>
            <div style={{
              width: '100%', height: '10px', background: '#e2e4e8',
              borderRadius: '5px', overflow: 'hidden', marginBottom: '8px',
            }}>
              <div style={{
                width: `${pushProgress}%`, height: '100%',
                background: colors.greenDark, borderRadius: '5px',
                transition: 'width 0.2s ease',
              }} />
            </div>
            <div style={{ fontSize: '0.78rem', color: colors.textSecondary }}>{pushProgress}%</div>
          </div>
        </div>
      );
    }

    const hasErrors = pushResults.errors.length > 0;
    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Part Out" icon="⊟" />
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.border}`,
          borderRadius: '6px',
          padding: '40px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px', color: hasErrors ? '#ea580c' : colors.greenDark }}>
            {hasErrors ? '⚠' : '✓'}
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: 800, color: hasErrors ? '#ea580c' : colors.greenDark, marginBottom: '16px' }}>
            {hasErrors ? 'Push Complete with Errors' : 'Push Complete!'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {pushResults.mergeOk > 0 && (
              <div style={{ fontSize: '0.85rem', color: colors.textPrimary }}>
                <span style={{ fontWeight: 800, color: '#2563eb' }}>{pushResults.mergeOk}</span> merged
              </div>
            )}
            {pushResults.newOk > 0 && (
              <div style={{ fontSize: '0.85rem', color: colors.textPrimary }}>
                <span style={{ fontWeight: 800, color: colors.greenDark }}>{pushResults.newOk}</span> new in -INCOMING
              </div>
            )}
          </div>
          {pushResults.errors.length > 0 && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
              padding: '14px', marginBottom: '20px', textAlign: 'left',
            }}>
              {pushResults.errors.map((err, i) => (
                <div key={i} style={{ fontSize: '0.75rem', color: '#dc2626', marginBottom: '4px' }}>{err}</div>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={launchStocking}
              style={{
                padding: '12px 24px', background: colors.greenDark, color: '#fff',
                border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
              }}
            >Start Stocking Mode →</button>
            <button
              onClick={() => { clearQueue(); setPushResults(null); setView('lookup'); }}
              style={{
                padding: '12px 24px', background: colors.navy, color: '#fff',
                border: 'none', borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
                fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
              }}
            >Part Out Another Set</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Render: Queue View ───
  if (view === 'queue') {
    const totalQueueParts = queue.reduce((sum, item) => sum + item.qty, 0);
    const mergeCount = queue.filter(item => item.qty > 0 && findExistingLot(item.partNo, item.colorId)).length;
    const newCount = queue.filter(item => item.qty > 0 && !findExistingLot(item.partNo, item.colorId)).length;

    return (
      <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
        <PageHeader title="Part Out" icon="⊟" />

        <button
          onClick={() => setView('lookup')}
          style={{
            background: 'none', border: 'none', color: colors.navy,
            fontFamily: "'Outfit', sans-serif", fontSize: '0.82rem', fontWeight: 700,
            cursor: 'pointer', padding: '0 0 12px 0', textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >← Add More Sets</button>

        {/* Queue stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
          {[
            { label: 'Sets', value: queuedSets.length, accent: colors.navy },
            { label: 'Lots', value: queue.filter(q => q.qty > 0).length, accent: colors.navy },
            { label: 'Merge', value: mergeCount, accent: '#2563eb' },
            { label: 'New', value: newCount, accent: colors.greenDark },
          ].map((s, i) => (
            <div key={i} style={{
              background: colors.cardBg, border: `1px solid ${colors.border}`,
              borderBottom: `3px solid ${s.accent}`, borderRadius: '6px',
              padding: '12px', textAlign: 'center',
            }}>
              <div style={{ fontSize: '1.3rem', fontWeight: 800, color: s.accent }}>{s.value}</div>
              <div style={{ fontSize: '0.62rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '3px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Queued sets list */}
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
          padding: '10px 14px', marginBottom: '16px', fontSize: '0.78rem', color: '#1e40af',
        }}>
          {queuedSets.map((s, i) => <div key={i} style={{ fontWeight: 600 }}>✓ {s}</div>)}
        </div>

        {/* Queue items */}
        <div style={{ marginBottom: '20px' }}>
          {queue.filter(item => item.qty > 0).map((item, i) => {
            const existing = findExistingLot(item.partNo, item.colorId);
            return (
              <div key={i} style={{
                background: colors.cardBg, border: `1px solid ${colors.border}`,
                borderLeft: `4px solid ${existing ? '#2563eb' : colors.greenDark}`,
                borderRadius: '6px', padding: '10px 12px', marginBottom: '6px',
                display: 'flex', alignItems: 'center', gap: '10px',
              }}>
                <img
                  src={getImageUrl(item.type, item.partNo, item.colorId)}
                  alt={item.partNo}
                  style={{ width: '40px', height: '40px', objectFit: 'contain', background: '#f9fafb', borderRadius: '6px', padding: '3px', flexShrink: 0 }}
                  onError={e => { e.target.style.opacity = '0.15'; }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary }}>{item.partNo}</div>
                  <div style={{ fontSize: '0.65rem', color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.color}</div>
                  {existing && (
                    <div style={{ fontSize: '0.6rem', color: '#2563eb', marginTop: '2px' }}>
                      Has {existing.quantity} · will add {item.qty} → {existing.quantity + item.qty}
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  value={item.qty}
                  min={0}
                  onChange={e => updateQueueQty(i, parseInt(e.target.value) || 0)}
                  style={{
                    width: '55px', padding: '6px', background: '#f9fafb',
                    border: `1px solid ${colors.border}`, borderRadius: '6px',
                    fontFamily: "'Outfit', sans-serif", fontSize: '0.9rem', fontWeight: 700,
                    color: colors.textPrimary, textAlign: 'center', outline: 'none',
                  }}
                />
                <button
                  onClick={() => removeFromQueue(i)}
                  style={{
                    background: 'none', border: 'none', color: '#dc2626',
                    fontSize: '0.8rem', cursor: 'pointer', padding: '4px',
                  }}
                >✕</button>
              </div>
            );
          })}
        </div>

        {/* Push button */}
        <button
          onClick={pushToBrickLink}
          disabled={queue.filter(q => q.qty > 0).length === 0}
          style={{
            width: '100%', padding: '16px', background: colors.navy,
            color: '#fff', border: 'none', borderRadius: '6px',
            fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '1rem',
            cursor: 'pointer', marginBottom: '10px',
          }}
        >
          Push {queue.filter(q => q.qty > 0).length} lots to BrickLink
        </button>

        <button
          onClick={clearQueue}
          style={{
            width: '100%', padding: '12px', background: colors.cardBg,
            color: colors.textSecondary, border: `1px solid ${colors.border}`,
            borderRadius: '6px', fontFamily: "'Outfit', sans-serif",
            fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
          }}
        >Clear Queue</button>
      </div>
    );
  }

  // ─── Render: Set Lookup ───
  const totalParts = parts.reduce((sum, p, i) => sum + (partQtys[i] ?? p.qty * copies), 0);
  const totalFigs = minifigs.reduce((sum, f, i) => sum + (figQtys[i] ?? f.qty * copies), 0);
  const totalLots = parts.filter((_, i) => (partQtys[i] ?? 0) > 0).length + minifigs.filter((_, i) => (figQtys[i] ?? 0) > 0).length;

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
      <PageHeader title="Part Out" icon="⊟" />

      {/* Queue badge */}
      {queue.length > 0 && (
        <button
          onClick={() => setView('queue')}
          style={{
            width: '100%', padding: '12px 16px', marginBottom: '16px',
            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
          }}
        >
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>
            Queue: {queue.length} lots from {queuedSets.length} set{queuedSets.length !== 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e40af' }}>
            Review & Push →
          </span>
        </button>
      )}

      {/* Search */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <input
          type="text"
          placeholder="Set number (e.g. 10297 or 10297-1)"
          value={setNumber}
          onChange={(e) => setSetNumber(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && lookupSet()}
          style={{
            flex: 1, padding: '14px 16px', background: colors.cardBg,
            border: `1px solid ${colors.border}`, borderRadius: '6px',
            color: colors.textPrimary, fontFamily: "'Outfit', sans-serif",
            fontSize: '0.95rem', outline: 'none',
          }}
        />
        <button
          onClick={lookupSet}
          disabled={loading}
          style={{
            padding: '14px 24px', background: loading ? colors.border : colors.navy,
            color: loading ? colors.textSecondary : '#ffffff', border: 'none',
            borderRadius: '6px', fontSize: '0.95rem', fontWeight: 800,
            fontFamily: "'Outfit', sans-serif", cursor: loading ? 'default' : 'pointer',
          }}
        >{loading ? '...' : 'Look Up'}</button>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
          padding: '14px 16px', marginBottom: '16px', fontSize: '0.85rem',
          color: '#dc2626', fontWeight: 600,
        }}>{error}</div>
      )}

      {/* Empty state */}
      {!setData && !loading && !error && (
        <div style={{
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderRadius: '6px', padding: '40px', textAlign: 'center',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '12px', color: colors.navy }}>⊟</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>
            Enter a set number above
          </div>
          <div style={{ fontSize: '0.85rem', color: colors.textSecondary }}>
            Look up a set, adjust quantities, and add to queue
          </div>
        </div>
      )}

      {/* Set Results */}
      {setData && (
        <>
          {/* Set header */}
          <div style={{
            background: colors.cardBg, border: `1px solid ${colors.border}`,
            borderLeft: `3px solid ${colors.navy}`, borderRadius: '6px',
            padding: '16px', display: 'flex', alignItems: 'center', gap: '16px',
            marginBottom: '16px',
          }}>
            <img
              src={`https://img.bricklink.com/ItemImage/SN/0/${setData.no}.png`}
              alt={setData.name}
              style={{
                width: '80px', height: '80px', objectFit: 'contain',
                background: '#f9fafb', borderRadius: '6px', padding: '4px',
                border: `1px solid ${colors.border}`,
              }}
              onError={(e) => { e.target.style.opacity = '0.15'; }}
            />
            {/* Smart Consolidation Legend */}
<div style={{
  background: '#f8f9fa',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  padding: '10px 12px',
  marginBottom: '14px',
  fontFamily: "'Outfit', sans-serif",
}}>
  <div style={{
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: '#6b7280',
    marginBottom: '6px',
  }}>
    Bin badge legend
  </div>
  <div style={{
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 16px',
    fontSize: '0.72rem',
    color: '#374151',
  }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <span style={{ width: '10px', height: '10px', background: '#86efac', border: '1px solid #22c55e', borderRadius: '2px' }}></span>
      Suggested bin
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <span style={{ width: '10px', height: '10px', background: '#bfdbfe', border: '1px solid #2563eb', borderRadius: '2px' }}></span>
      Same part + color
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <span style={{ width: '10px', height: '10px', background: '#fef08a', border: '1px solid #ca8a04', borderRadius: '2px' }}></span>
      In a tray
    </span>
    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <span style={{ width: '10px', height: '10px', background: '#e9d5ff', border: '1px solid #9333ea', borderRadius: '2px' }}></span>
      Different color
    </span>
  </div>
</div>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 800, color: colors.textPrimary, marginBottom: '4px' }}>
                {setData.name}
              </div>
              <div style={{ fontSize: '0.82rem', color: colors.textSecondary }}>
                {setData.no} · {setData.year_released} · {setData.weight}g
              </div>
            </div>
          </div>

          {/* Copies control */}
          <div style={{
            background: colors.cardBg, border: `1px solid ${colors.border}`,
            borderRadius: '6px', padding: '14px', marginBottom: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: colors.textPrimary }}>Copies</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => handleCopiesChange(copies - 1)}
                disabled={copies <= 1}
                style={{
                  width: '32px', height: '32px', background: copies <= 1 ? '#f9fafb' : colors.navy,
                  color: copies <= 1 ? colors.border : '#fff', border: `1px solid ${colors.border}`,
                  borderRadius: '6px', fontSize: '1rem', fontWeight: 800, cursor: copies <= 1 ? 'not-allowed' : 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}
              >-</button>
              <span style={{ fontSize: '1.2rem', fontWeight: 800, color: colors.navy, minWidth: '30px', textAlign: 'center' }}>{copies}</span>
              <button
                onClick={() => handleCopiesChange(copies + 1)}
                style={{
                  width: '32px', height: '32px', background: colors.navy,
                  color: '#fff', border: 'none', borderRadius: '6px',
                  fontSize: '1rem', fontWeight: 800, cursor: 'pointer',
                  fontFamily: "'Outfit', sans-serif",
                }}
              >+</button>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
            {[
              { label: 'Total Lots', value: totalLots, accent: colors.navy },
              { label: 'Total Pieces', value: totalParts + totalFigs, accent: colors.navy },
              { label: 'Minifigs', value: minifigs.length, accent: '#d97706' },
            ].map((s, i) => (
              <div key={i} style={{
                background: colors.cardBg, border: `1px solid ${colors.border}`,
                borderBottom: `3px solid ${s.accent}`, borderRadius: '6px',
                padding: '12px', textAlign: 'center',
              }}>
                <div style={{ fontSize: '1.3rem', fontWeight: 800, color: s.accent }}>{s.value}</div>
                <div style={{ fontSize: '0.62rem', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: '3px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Parts grid */}
          {parts.length > 0 && (
            <>
              <div style={{
                fontSize: '0.72rem', fontWeight: 700, color: colors.textSecondary,
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px',
              }}>Parts ({parts.length})</div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '10px', marginBottom: '20px',
              }}>
                {parts.map((part, i) => (
                  <div key={i} style={{
                    background: colors.cardBg, border: `1px solid ${colors.border}`,
                    borderRadius: '6px', padding: '12px 10px', textAlign: 'center',
                  }}>
                    <div style={{ position: 'relative', marginBottom: '6px' }}>
                      <img
                        src={getImageUrl(part.type, part.partNo, part.colorId)}
                        alt={part.name}
                        style={{ width: '100%', maxHeight: '80px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.opacity = '0.15'; }}
                      />
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        const typeMap = { P: 'P', PART: 'P', M: 'M', MINIFIG: 'M', S: 'S', SET: 'S', G: 'G', GEAR: 'G' };
                        const blType = typeMap[part.type] || 'P';
                        window.open(`https://www.bricklink.com/v2/catalog/catalogitem.page?${blType}=${part.partNo}`, '_blank');
                      }}
                      style={{
                        fontSize: '0.72rem', fontWeight: 800, color: colors.navy,
                        textDecoration: 'underline', textDecorationColor: 'rgba(7,26,82,0.3)',
                        textUnderlineOffset: '2px', cursor: 'pointer', marginBottom: '2px',
                      }}
                    >{part.partNo}</div>
                    <div style={{
                      fontSize: '0.62rem', color: colors.textSecondary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{part.name}</div>
                    <div style={{ fontSize: '0.62rem', color: colors.textSecondary }}>{part.color}</div>
                    <div style={{ marginTop: '6px' }}>
                      <input
                        type="number"
                        value={partQtys[i] ?? part.qty * copies}
                        min={0}
                        onChange={e => setPartQtys(prev => ({ ...prev, [i]: parseInt(e.target.value) || 0 }))}
                        style={{
                          width: '60px', padding: '4px 6px', background: '#f9fafb',
                          border: `1px solid ${colors.border}`, borderRadius: '4px',
                          fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', fontWeight: 700,
                          color: colors.textPrimary, textAlign: 'center', outline: 'none',
                        }}
                      />
                    </div>
                    <div style={{ marginTop: '6px' }}>{renderBinBadge(part.partNo, part.color)}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Minifigs grid */}
          {minifigs.length > 0 && (
            <>
              <div style={{
                fontSize: '0.72rem', fontWeight: 700, color: colors.textSecondary,
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px',
              }}>Minifigs ({minifigs.length})</div>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '10px', marginBottom: '20px',
              }}>
                {minifigs.map((fig, i) => (
                  <div key={i} style={{
                    background: colors.cardBg, border: `1px solid ${colors.border}`,
                    borderTop: '3px solid #d97706', borderRadius: '6px',
                    padding: '12px 10px', textAlign: 'center',
                  }}>
                    <div style={{ position: 'relative', marginBottom: '6px' }}>
                      <img
                        src={getImageUrl(fig.type, fig.partNo, fig.colorId)}
                        alt={fig.name}
                        style={{ width: '100%', maxHeight: '80px', objectFit: 'contain' }}
                        onError={(e) => { e.target.style.opacity = '0.15'; }}
                      />
                    </div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`https://www.bricklink.com/v2/catalog/catalogitem.page?M=${fig.partNo}`, '_blank');
                      }}
                      style={{
                        fontSize: '0.72rem', fontWeight: 800, color: colors.navy,
                        textDecoration: 'underline', textDecorationColor: 'rgba(7,26,82,0.3)',
                        textUnderlineOffset: '2px', cursor: 'pointer', marginBottom: '2px',
                      }}
                    >{fig.partNo}</div>
                    <div style={{
                      fontSize: '0.62rem', color: colors.textSecondary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>{fig.name}</div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#d97706', marginTop: '4px' }}>Minifig</div>
                    <div style={{ marginTop: '6px' }}>
                      <input
                        type="number"
                        value={figQtys[i] ?? fig.qty * copies}
                        min={0}
                        onChange={e => setFigQtys(prev => ({ ...prev, [i]: parseInt(e.target.value) || 0 }))}
                        style={{
                          width: '60px', padding: '4px 6px', background: '#f9fafb',
                          border: `1px solid ${colors.border}`, borderRadius: '4px',
                          fontFamily: "'Outfit', sans-serif", fontSize: '0.85rem', fontWeight: 700,
                          color: colors.textPrimary, textAlign: 'center', outline: 'none',
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Instructions button */}
          <button
            onClick={() => {
              const cleanNo = setData.no.replace(/-\d+$/, '');
              window.open(`https://www.lego.com/en-us/service/buildinginstructions/${cleanNo}`, '_blank');
            }}
            style={{
              width: '100%', padding: '14px 24px', background: colors.cardBg,
              color: colors.navy, border: `2px solid ${colors.navy}`,
              borderRadius: '6px', fontSize: '1rem', fontWeight: 800,
              fontFamily: "'Outfit', sans-serif", cursor: 'pointer', marginBottom: '10px',
            }}
          >View Building Instructions ↗</button>

          {/* Add to Queue button */}
          <button
            onClick={addToQueue}
            style={{
              width: '100%', padding: '16px', background: colors.greenDark,
              color: '#fff', border: 'none', borderRadius: '6px',
              fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '1rem',
              cursor: 'pointer', marginBottom: '10px',
            }}
          >Add to Queue ({totalLots} lots, {totalParts + totalFigs} pieces)</button>

          {/* Or push directly if no queue */}
          {queue.length === 0 && (
            <button
              onClick={() => { addToQueue(); setTimeout(() => setView('queue'), 100); }}
              style={{
                width: '100%', padding: '14px', background: colors.navy,
                color: '#fff', border: 'none', borderRadius: '6px',
                fontFamily: "'Outfit', sans-serif", fontWeight: 800, fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >Add & Push to BrickLink →</button>
          )}
        </>
      )}
    </div>
  );
}

export default PartOut;